import {
    Paragraph,
    TextRun,
    Table,
    TableRow,
    TableCell,
    WidthType,
    BorderStyle,
    AlignmentType,
    VerticalAlign,
    HeightRule,
    TableLayoutType,
} from "docx";
import { fixColor, fitImage, fetchImageWithDimensions, FULL_WIDTH, SCALE, createCommonHeader, createCommonFooter } from "../../common.js";

export const createTOCTemp2 = async (tocData, startPage) => {
    const style = tocData.style || {};
    const sections = [];

    const FULL_WIDTH_DXA = FULL_WIDTH;
    const LEFT_COL_PERCENT = 35;
    const RIGHT_COL_PERCENT = 65;

    const leftColWidth = Math.floor(FULL_WIDTH_DXA * (LEFT_COL_PERCENT / 100));
    const rightColWidth = Math.floor(FULL_WIDTH_DXA * (RIGHT_COL_PERCENT / 100));

    // Estimation Constants
    const pointMultiplier = 11;
    const baseFontSize = style.text?.size || 12;
    const renderedFontSize = baseFontSize * SCALE;
    const LINE_EST_HEIGHT = renderedFontSize * pointMultiplier;
    const ITEM_SPACING = 350; // Increased even more for better breathing room
    const CHARS_PER_LINE = Math.floor(750 / renderedFontSize); // More liberal line count

    const SECTION_LIMIT_DXA = 5100; // Reduced by 400
    const MIDDLE_IMG_HEIGHT = 5300;

    const projects = tocData.projects || [];
    let itemQueue = [...(tocData.pages || [])];
    let projIndex = 0;
    let pageCount = 0;
    let currentAbsPage = startPage || 1;

    // Helper to estimate item height
    const getItemHeight = (item) => {
        const text = item.heading || "";
        const numLines = Math.ceil(text.length / CHARS_PER_LINE) || 1;
        return (numLines * LINE_EST_HEIGHT) + ITEM_SPACING;
    };

    // Helper to create item list using a nested table for better layout control
    const createItemList = (items) => {
        if (items.length === 0) return [new Paragraph("")];
        return [
            new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                    top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
                },
                rows: items.map(item => new TableRow({
                    children: [
                        new TableCell({
                            width: { size: 90, type: WidthType.PERCENTAGE },
                            children: [
                                new Paragraph({
                                    spacing: { after: 400 },
                                    children: [
                                        new TextRun({
                                            text: item.heading || "",
                                            bold: style.text?.bold,
                                            italics: style.text?.italic,
                                            underline: style.text?.underline ? {} : undefined,
                                            color: fixColor(style.text?.color || "#000000"),
                                            size: (style.text?.size || 12) * SCALE,
                                            font: style.text?.font
                                        })
                                    ]
                                })
                            ]
                        }),
                        new TableCell({
                            width: { size: 10, type: WidthType.PERCENTAGE },
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.RIGHT,
                                    spacing: { after: 200 },
                                    children: [
                                        new TextRun({
                                            text: String(item.page).padStart(2, '0'),
                                            bold: style.label?.bold,
                                            italics: style.label?.italic,
                                            underline: style.label?.underline ? {} : undefined,
                                            color: fixColor(style.label?.color || "#000000"),
                                            size: (style.label?.size || 12) * SCALE,
                                            font: style.label?.font
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }))
            })
        ];
    };

    while (itemQueue.length > 0 && pageCount < 50) {
        pageCount++;

        // 1. Fill Top Items
        const topItems = [];
        let currentTopHeight = 0;
        while (itemQueue.length > 0) {
            const h = getItemHeight(itemQueue[0]);
            if (currentTopHeight + h <= SECTION_LIMIT_DXA || topItems.length === 0) {
                topItems.push(itemQueue.shift());
                currentTopHeight += h;
            } else {
                break;
            }
        }

        // 2. Fill Bottom Items
        const bottomItems = [];
        let currentBottomHeight = 0;
        while (itemQueue.length > 0) {
            const h = getItemHeight(itemQueue[0]);
            if (currentBottomHeight + h <= SECTION_LIMIT_DXA || bottomItems.length === 0) {
                bottomItems.push(itemQueue.shift());
                currentBottomHeight += h;
            } else {
                break;
            }
        }

        const project = projects[projIndex++] || null;
        const pageRows = [];

        // --- Top Row ---
        pageRows.push(new TableRow({
            height: { value: 4000, rule: HeightRule.AT_LEAST },
            children: [
                new TableCell({
                    width: { size: LEFT_COL_PERCENT, type: WidthType.PERCENTAGE },
                    verticalAlign: VerticalAlign.CENTER,
                    margins: { right: 600 },
                    borders: { right: { style: BorderStyle.SINGLE, size: 24, color: "000000" } },
                    children: (pageCount === 1) ? [
                        new Paragraph({
                            alignment: AlignmentType.RIGHT,
                            children: [
                                new TextRun({
                                    text: (tocData.title).toUpperCase(),
                                    bold: style.heading?.bold || true,
                                    italics: style.heading?.italic,
                                    underline: style.heading?.underline ? {} : undefined,
                                    color: fixColor(style.heading?.color || "#000000"),
                                    size: (style.heading?.size || 24) * SCALE,
                                    font: style.heading?.font
                                })
                            ]
                        })
                    ] : []
                }),
                new TableCell({
                    width: { size: RIGHT_COL_PERCENT, type: WidthType.PERCENTAGE },
                    margins: { left: 400, right: 800 },
                    children: createItemList(topItems)
                })
            ]
        }));

        // Spacer Row before Image
        pageRows.push(new TableRow({
            height: { value: 800, rule: HeightRule.EXACT },
            children: [
                new TableCell({ columnSpan: 2, children: [new Paragraph("")] })
            ]
        }));

        // --- Middle Row (Image) ---
        let imgRun = null;
        const photo = project?.uploadedPhoto || project?.photoURL;
        if (project && photo) {
            const imgData = await fetchImageWithDimensions(photo);
            if (imgData) {
                // Image covers full page width
                imgRun = fitImage(imgData, FULL_WIDTH_DXA, MIDDLE_IMG_HEIGHT, "cover");
            }
        }

        pageRows.push(new TableRow({
            height: { value: MIDDLE_IMG_HEIGHT, rule: HeightRule.EXACT },
            children: [
                new TableCell({
                    columnSpan: 2,
                    shading: (project && project.photoURL && imgRun) ? undefined : { fill: fixColor(tocData.style.colorScheme?.body) },
                    verticalAlign: VerticalAlign.CENTER,
                    children: imgRun ? [new Paragraph({ alignment: AlignmentType.CENTER, children: [imgRun] })] : [new Paragraph("")]
                })
            ]
        }));

        // Spacer Row after Image
        pageRows.push(new TableRow({
            height: { value: 800, rule: HeightRule.EXACT },
            children: [
                new TableCell({ columnSpan: 2, children: [new Paragraph("")] })
            ]
        }));

        // --- Bottom Row ---
        pageRows.push(new TableRow({
            height: { value: 4000, rule: HeightRule.AT_LEAST },
            children: [
                new TableCell({
                    width: { size: LEFT_COL_PERCENT, type: WidthType.PERCENTAGE },
                    borders: { right: { style: BorderStyle.SINGLE, size: 24, color: "000000" } },
                    children: []
                }),
                new TableCell({
                    width: { size: RIGHT_COL_PERCENT, type: WidthType.PERCENTAGE },
                    margins: { left: 400, right: 800 },
                    children: createItemList(bottomItems)
                })
            ]
        }));

        const pageTable = new Table({
            width: { size: FULL_WIDTH_DXA, type: WidthType.DXA },
            rows: pageRows,
            layout: TableLayoutType.FIXED,
            borders: {
                top: { style: BorderStyle.NONE }, bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE }, right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE }, insideVertical: { style: BorderStyle.NONE }
            }
        });

        sections.push({
            properties: { page: { margin: { top: 800, right: 0, bottom: 800, left: 0 } } }, // Zero lateral margins
            children: [pageTable]
        });
        currentAbsPage++;
    }

    // Default empty page if nothing generated
    if (sections.length === 0) {
        sections.push({
            children: [new Paragraph({ text: " " })]
        });
    }

    return sections;
};
